import { Button } from '@components/base/actions';
import { VStack, HStack, Stack } from '@components/base/layout';
import { Text } from '@components/base/display';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

const gaps = [0, 1, 2, 4, 6, 8] as const;
const alignOptions = ['start', 'center', 'end', 'stretch'] as const;
const justifyOptions = ['start', 'center', 'end', 'between', 'around'] as const;

function DemoBox({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="bg-[#E8E6E3] px-3 py-2 rounded text-sm">
      {children}
    </div>
  );
}

function StackShowcaseComponent({ playgroundProps, isDarkMode: _isDarkMode }: Readonly<ShowcaseProps>) {
  const direction = playgroundProps.direction as 'vertical' | 'horizontal';
  const StackComponent = direction === 'vertical' ? VStack : HStack;

  return (
    <VStack gap={6}>
      {/* Interactive Preview */}
      <ShowcasePreviewSection componentName="stack">
          <div className="p-4 border border-dashed border-[#D4D1CC] rounded min-h-32">
            <StackComponent
              gap={Number(playgroundProps.gap) as 0 | 1 | 2 | 4 | 6 | 8}
              align={playgroundProps.align as 'start' | 'center' | 'end' | 'stretch'}
              justify={playgroundProps.justify as 'start' | 'center' | 'end' | 'between' | 'around'}
            >
              <DemoBox>Item 1</DemoBox>
              <DemoBox>Item 2</DemoBox>
              <DemoBox>Item 3</DemoBox>
            </StackComponent>
          </div>
      </ShowcasePreviewSection>

      {/* VStack Examples */}
      <ShowcaseDemoSection
        title="VStack (Vertical Stack)"
        description="Arranges children vertically with consistent spacing"
       
      >
          <HStack gap={8} wrap="wrap" align="start">
            <VStack gap={4} className="w-48">
              <Text size="sm" weight="semibold" color="muted">Form Layout</Text>
              <VStack gap={3}>
                <DemoBox>Label + Input</DemoBox>
                <DemoBox>Label + Input</DemoBox>
                <DemoBox>Label + Input</DemoBox>
                <DemoBox>Submit Button</DemoBox>
              </VStack>
            </VStack>
            <VStack gap={4} className="w-48">
              <Text size="sm" weight="semibold" color="muted">Card Content</Text>
              <VStack gap={2}>
                <DemoBox>Title</DemoBox>
                <DemoBox>Description</DemoBox>
                <DemoBox>Meta info</DemoBox>
              </VStack>
            </VStack>
          </HStack>
      </ShowcaseDemoSection>

      {/* HStack Examples */}
      <ShowcaseDemoSection
        title="HStack (Horizontal Stack)"
        description="Arranges children horizontally with consistent spacing"
       
      >
          <VStack gap={6}>
            <VStack gap={2}>
              <Text size="sm" weight="semibold" color="muted">Button Group</Text>
              <HStack gap={2}>
                <Button variant="ghost" size="sm">Cancel</Button>
                <Button variant="secondary" size="sm">Save Draft</Button>
                <Button variant="primary" size="sm">Submit</Button>
              </HStack>
            </VStack>
            <VStack gap={2}>
              <Text size="sm" weight="semibold" color="muted">Navigation Tabs</Text>
              <HStack gap={4}>
                <DemoBox>Overview</DemoBox>
                <DemoBox>Policies</DemoBox>
                <DemoBox>Claims</DemoBox>
                <DemoBox>Documents</DemoBox>
              </HStack>
            </VStack>
          </VStack>
      </ShowcaseDemoSection>

      {/* Alignment Options */}
      <ShowcaseDemoSection
        title="Alignment Options"
        description="Control cross-axis alignment with the align prop"
       
      >
          <VStack gap={6}>
            {alignOptions.map((align) => (
              <VStack key={align} gap={1}>
                <Text size="xs" color="muted" className="capitalize">{align}</Text>
                <div className="p-4 border border-dashed border-[#D4D1CC] rounded h-24">
                  <HStack gap={2} align={align} className="h-full">
                    <div className="bg-[#E8E6E3] px-3 py-1 rounded text-sm">Short</div>
                    <div className="bg-[#E8E6E3] px-3 py-4 rounded text-sm">Tall</div>
                    <div className="bg-[#E8E6E3] px-3 py-2 rounded text-sm">Medium</div>
                  </HStack>
                </div>
              </VStack>
            ))}
          </VStack>
      </ShowcaseDemoSection>

      {/* Justify Options */}
      <ShowcaseDemoSection
        title="Justify Options"
        description="Control main-axis distribution with the justify prop"
       
      >
          <VStack gap={6}>
            {justifyOptions.map((justify) => (
              <VStack key={justify} gap={1}>
                <Text size="xs" color="muted" className="capitalize">{justify}</Text>
                <div className="p-4 border border-dashed border-[#D4D1CC] rounded">
                  <HStack gap={2} justify={justify}>
                    <DemoBox>A</DemoBox>
                    <DemoBox>B</DemoBox>
                    <DemoBox>C</DemoBox>
                  </HStack>
                </div>
              </VStack>
            ))}
          </VStack>
      </ShowcaseDemoSection>

      {/* Gap Options */}
      <ShowcaseDemoSection
        title="Gap Options"
        description="Control spacing between items (0-16 maps to 0-64px)"
       
      >
          <VStack gap={4}>
            {gaps.map((gap) => (
              <VStack key={gap} gap={1}>
                <Text size="xs" color="muted">gap={gap} ({gap * 4}px)</Text>
                <div className="p-4 border border-dashed border-[#D4D1CC] rounded">
                  <HStack gap={gap}>
                    <DemoBox>1</DemoBox>
                    <DemoBox>2</DemoBox>
                    <DemoBox>3</DemoBox>
                  </HStack>
                </div>
              </VStack>
            ))}
          </VStack>
      </ShowcaseDemoSection>

      {/* Nested Stacks */}
      <ShowcaseDemoSection
        title="Nested Stacks"
        description="Stacks can be nested to create complex layouts"
       
      >
          <div className="max-w-md p-4 border border-dashed border-[#D4D1CC] rounded">
            <VStack gap={4}>
              <Text size="sm" weight="semibold">Insurance Quote Form</Text>
              <HStack gap={4}>
                <VStack gap={2} className="flex-1">
                  <DemoBox>First Name</DemoBox>
                  <DemoBox>Date of Birth</DemoBox>
                </VStack>
                <VStack gap={2} className="flex-1">
                  <DemoBox>Last Name</DemoBox>
                  <DemoBox>Phone</DemoBox>
                </VStack>
              </HStack>
              <DemoBox>Email Address</DemoBox>
              <HStack gap={2} justify="end">
                <Button variant="ghost" size="sm">Clear</Button>
                <Button variant="primary" size="sm">Get Quote</Button>
              </HStack>
            </VStack>
          </div>
      </ShowcaseDemoSection>

      {/* With Dividers */}
      <ShowcaseDemoSection
        title="With Dividers"
        description="Stacks can render dividers between items using the divider prop"
       
      >
          <HStack gap={8} wrap="wrap" align="start">
            <VStack gap={4} className="w-64">
              <Text size="sm" weight="semibold" color="muted">Vertical with dividers</Text>
              <Stack direction="column" divider>
                <div className="py-2">Policy Information</div>
                <div className="py-2">Coverage Details</div>
                <div className="py-2">Payment History</div>
              </Stack>
            </VStack>
            <VStack gap={4}>
              <Text size="sm" weight="semibold" color="muted">Horizontal with dividers</Text>
              <div className="h-8 flex items-center">
                <Stack direction="row" align="center" divider>
                  <span className="px-2">Home</span>
                  <span className="px-2">Policies</span>
                  <span className="px-2">Claims</span>
                </Stack>
              </div>
            </VStack>
          </HStack>
      </ShowcaseDemoSection>
    </VStack>
  );
}

export const StackShowcase: ShowcaseDefinition = {
  id: 'stack',
  name: 'Stack',
  category: 'layout',
  description: 'Flexible layout components for arranging children (VStack, HStack)',
  propsDefinition: [
    {
      name: 'direction',
      type: 'select',
      options: ['vertical', 'horizontal'],
      defaultValue: 'vertical',
      description: 'Direction of the stack (uses VStack or HStack)',
    },
    {
      name: 'gap',
      type: 'select',
      options: ['0', '1', '2', '4', '6', '8'],
      defaultValue: '4',
      description: 'Gap between items (multiplied by 4px)',
    },
    {
      name: 'align',
      type: 'select',
      options: ['start', 'center', 'end', 'stretch'],
      defaultValue: 'stretch',
      description: 'Cross-axis alignment of items',
    },
    {
      name: 'justify',
      type: 'select',
      options: ['start', 'center', 'end', 'between', 'around'],
      defaultValue: 'start',
      description: 'Main-axis distribution of items',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'Uses semantic <div> by default',
      'Can use as prop for semantic elements (nav, section)',
    ],
    keyboardNav: [
      'Tab: Navigate through focusable children',
    ],
    notes: [
      'Maintains logical reading order',
      'Use semantic elements when appropriate (as="nav")',
      'Dividers have role="separator"',
    ],
  },
  component: StackShowcaseComponent,
};
